import React, { useEffect, useState, useMemo } from 'react';
import Page from '../../../components/Page';
import useSettings from '../../../hooks/useSettings';
import { useNavigate, useParams } from 'react-router-dom';
import { dispatch, useSelector } from '../../../redux/store';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { getGuestFromBard } from '../../../redux/slices/guestRedux/guestRedux';
import { Box, Button, Container, Grid, Stack, Tab, TextField, Typography, Paper } from '@mui/material';
import { getFoods } from '../../../redux/slices/stockManagementRedux/foodRedux';
import GuestInvoice from './component/guestInvoice';
import ProductSelection from './component/productSelection';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import CustomSale from './component/customSale';
import BillingPayments from './component/billingPayments';

const Billing = () => {
  const { id } = useParams();
  const pageName = 'Billing';
  const { themeStretch } = useSettings();
  const navigate = useNavigate();
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('shopSale');
  const { pos } = useSelector((state) => state?.guest);
  const [selectedCategory, setSelectedCategory] = useState('');
  const { data: menu } = useSelector((state) => state.food);

  const focusStyles = useMemo(
    () => ({
      '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'primary.main' },
      '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}33` },
    }),
    []
  );

  const handleSelectAll = (e) => e.target.select();

  const filteredMenu = useMemo(
    () =>
      menu
        ?.filter(
          (item) =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
            (selectedCategory === '' || item.category === selectedCategory)
        )
        ?.sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [menu, searchQuery, selectedCategory]
  );

  useEffect(() => {
    setIsDataLoaded(false);
    setSelectedData({});
    dispatch(getFoods('menu'));
  }, []);

  useEffect(() => {
    if (pos?.length > 0 && id !== 'Enter Here') {
      setIsDataLoaded(pos?.[0]?.guests?.id);
      setSelectedData(pos?.[0]);
    } else {
      setIsDataLoaded(false);
      setSelectedData({});
    }
  }, [pos, id]);

  useEffect(() => {
    localStorage.setItem('secondDisplay', JSON.stringify(selectedData));
  }, [selectedData]);

  const handleFromBarcodeEnter = (event) => {
    if (event.key === 'Enter') {
      event.target.select();
      const value = event.target.value;
      dispatch(getGuestFromBard(value));
    }
  };

  return (
    <Page title={`${pageName}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`${pageName}`}
          links={[]}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  window.open(`/secondDisplay`, '_blank');
                }}
              >
                View Display 2
              </Button>
              {isDataLoaded ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedData({});
                    setIsDataLoaded(false);
                    dispatch(getGuestFromBard('NEW_CARD_REQ'));
                  }}
                >
                  Scan New Card
                </Button>
              ) : (
                <TextField
                  autoFocus
                  margin="dense"
                  label="Scan Guest QR"
                  size="small"
                  sx={{ width: { xs: '100%', sm: '60%' }, ...focusStyles }}
                  value={id}
                  onChange={(event) => {
                    setIsDataLoaded(false);
                    navigate(`/dashboard/billing/${event.target.value}`);
                  }}
                  onClick={handleSelectAll}
                  onFocus={handleSelectAll}
                  onKeyDown={handleFromBarcodeEnter}
                />
              )}
            </Stack>
          }
        />

        <Grid container spacing={3}>
          {!isDataLoaded ? (
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 6 }}>
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="text.secondary">
                    Please scan the guest card to continue
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <TabContext value={selectedTab}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <TabList
                        onChange={(event, newValue) => {
                          setSelectedTab(newValue);
                        }}
                        aria-label="Billing Type"
                        variant="fullWidth"
                      >
                        <Tab label="Shop Sale" value="shopSale" />
                        <Tab label="Custom Sale" value="customSale" />
                        <Tab label="Mark Payment" value="markPayment" />
                      </TabList>
                    </Box>

                    <TabPanel value="shopSale" sx={{ px: 0 }}>
                      <TextField
                        fullWidth
                        label="Search products..."
                        margin="normal"
                        variant="outlined"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={handleSelectAll}
                        onFocus={handleSelectAll}
                        sx={{ mb: 3, ...focusStyles }}
                      />

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Array.from(new Set(menu?.map((item) => item.category)))
                            .filter(Boolean)
                            .map((category) => (
                              <Button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                variant={selectedCategory === category ? 'contained' : 'outlined'}
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                              >
                                {category}
                              </Button>
                            ))}
                          {selectedCategory !== '' && (
                            <Button
                              onClick={() => setSelectedCategory('')}
                              variant="outlined"
                              color="secondary"
                              sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                              Show All
                            </Button>
                          )}
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        {filteredMenu?.length === 0 ? (
                          <Grid item xs={12}>
                            <Typography align="center" sx={{ py: 4 }}>
                              No products found.
                            </Typography>
                          </Grid>
                        ) : (
                          <ProductSelection cardId={id} filteredMenu={filteredMenu} />
                        )}
                      </Grid>
                    </TabPanel>

                    <TabPanel value="customSale" sx={{ px: 0 }}>
                      <Grid container spacing={2}>
                        <CustomSale cardId={id} />
                      </Grid>
                    </TabPanel>

                    <TabPanel value="markPayment" sx={{ px: 0 }}>
                      <Grid container spacing={2}>
                        <BillingPayments cardId={id} selectedData={selectedData} />
                      </Grid>
                    </TabPanel>
                  </TabContext>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <GuestInvoice cardId={id} selectedData={selectedData} />
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </Page>
  );
};

export default Billing;
